(() => {
  'use strict';
  if (!window.customElements || customElements.get('tm-quote-builder')) return;
  const colours = { natural: '#eee7d9', black: '#252525', white: '#fafaf6', grey: '#989b9c', navy: '#304058', green: '#40564b', orange: '#d85e27', custom: '#eee7d9' };
  const fits = { normal: 'Normal', oversize: 'Oversize', box: 'Box fit' };
  const designs = { yes: 'Yes', no: 'No', help: 'I want help with design from an expert' };
  class QuoteBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.controller.signal });
      this.find = selector => this.querySelector(selector);
      this.all = selector => [...this.querySelectorAll(selector)];
      try {
        this.config = JSON.parse(this.find('[data-quote-config]').textContent);
        if (!window.TMQuotePricing || !this.config.products?.length) throw new Error('Configuration unavailable');
        this.products = this.config.products;
        this.currency = ['EUR', 'GBP', 'USD'].includes(this.config.currency) ? this.config.currency : 'EUR';
        this.format = new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.currency });
        this.storageKey = `tm-quote-2810:${location.pathname}:${this.dataset.section}`;
        this.saved = {};
        try { this.saved = JSON.parse(sessionStorage.getItem(this.storageKey)) || {}; } catch (_) {}
        this.state = { product: 'hoodie', fit: 'normal', weight: 0, colour: 'natural', pms: '', quantity: '100', design: '', ...this.saved };
        const requested = new URL(location.href).searchParams.get('piece');
        if (this.products.some(p => p.key === requested) && requested !== this.state.product) {
          this.state.product = requested; this.state.fit = 'normal'; this.state.weight = 0;
        }
        if (!this.products.some(p => p.key === this.state.product)) this.state.product = this.products[0].key;
        if (!(this.state.fit in fits)) this.state.fit = 'normal';
        if (!(this.state.colour in colours)) this.state.colour = 'natural';
        if (!(this.state.design in designs)) this.state.design = '';
        if (!Number.isInteger(this.state.weight) || this.state.weight < 0 || this.state.weight > 2) this.state.weight = 0;
        this.state.pms = String(this.state.pms || '').slice(0, 60);
        this.side = 'front'; this.images = new Map();
        this.form = this.find('form');
        this.restoreDraft();
        // Shopify prints every contact[...] field. Keep specifications in one complete brief.
        // Filtering the serialized payload leaves field names, drafts and validation intact.
        this.on(this.form, 'formdata', event => {
          if (!this.result || !this.find('[data-quote-body]').value) return;
          this.updateBrief();
          event.formData.set('contact[body]', this.find('[data-quote-body]').value);
          for (const name of ['Fabric', 'Custom PMS', 'Quantity', 'Delivery destination', 'Target timing', 'Decoration', 'Artwork link', 'Additional details', 'Artwork files', 'Artwork placement', 'Product', 'Fit / style', 'Colour', 'Design readiness']) {
            event.formData.delete(`contact[${name}]`);
          }
          if (!String(event.formData.get('contact[Company]') || '').trim()) event.formData.delete('contact[Company]');
        });
        const select = this.find('[data-quote-select]'); select.replaceChildren();
        this.products.forEach(product => {
          const option = document.createElement('option'); option.value = product.key; option.textContent = product.title; select.append(option);
        });
        this.on(select, 'change', () => this.chooseProduct(select.value, true));
        this.find('[data-quote-mobile]').hidden = false;
        this.all('[data-quote-product]').forEach(button => {
          const p = this.products.find(p => p.key === button.dataset.quoteProduct);
          button.querySelector('[data-product-label]').textContent = p.title;
          this.on(button, 'click', () => this.chooseProduct(p.key, true));
        });
        this.on(this, 'change', event => {
          const el = event.target;
          if (el.matches('[data-quote-fit]')) this.state.fit = el.value;
          else if (el.matches('[data-quote-weight]')) this.state.weight = Number(el.dataset.quoteWeight);
          else if (el.matches('[data-quote-colour]')) { this.state.colour = el.value; this.draw(); }
          else if (el.matches('[data-quote-design]')) this.state.design = el.value;
          else return;
          this.update();
        });
        this.on(this.find('[data-quote-quantity]'), 'input', event => { this.state.quantity = event.target.value; this.update(); });
        this.on(this.find('[data-quote-pms]'), 'input', event => { this.state.pms = event.target.value; this.update(); });
        this.all('[data-quote-qty]').forEach(button => this.on(button, 'click', () => { this.state.quantity = button.dataset.quoteQty; this.update(); }));
        this.all('[data-quote-view]').forEach(button => this.on(button, 'click', () => { this.side = button.dataset.quoteView; this.draw(); }));
        this.on(this.find('[data-quote-open]'), 'click', () => this.openContact());
        this.on(this.find('[data-quote-edit]'), 'click', () => {
          this.artwork?.showPanel('details');
          this.find('[data-quote-fit]:checked').focus({ preventScroll: true });
          this.find('.tm-config__options').scrollIntoView({ behavior: this.motion(), block: 'start' });
        });
        // Capture on the ancestor: Shopify's form listener does not respect
        // defaultPrevented and would otherwise submit during asynchronous uploads.
        this.addEventListener('submit', event => {
          if (event.target !== this.form) return;
          event.preventDefault(); event.stopImmediatePropagation();
          this.sendQuote();
        }, { capture: true, signal: this.controller.signal });
        for (const type of ['click', 'pointerdown', 'pointermove', 'keydown', 'beforeinput', 'input', 'change', 'drop']) {
          this.addEventListener(type, event => {
            if (!this.quoteSending || !event.target.closest('button,input,select,textarea,canvas')) return;
            if (type === 'keydown' && event.key === 'Tab') return;
            event.preventDefault(); event.stopImmediatePropagation();
          }, { capture: true, signal: this.controller.signal });
        }
        this.on(this.form, 'formdata', () => {
          // Shopify can serialize once to save a draft, then once for the POST.
          if (this.quoteHandedOff) clearTimeout(this.quoteWatchdog);
        });
        this.on(window, 'pageshow', event => { if (event.persisted) this.releaseQuote(); });
        this.on(this.form, 'keydown', event => {
          if (event.key === 'Enter' && event.target.matches('input') && this.find('[data-quote-contact]').hidden) {
            event.preventDefault(); this.openContact();
          }
        });
        for (const el of this.draftFields()) {
          for (const event of ['input', 'change']) this.on(el, event, () => { this.saveDraft(); this.updateBrief(); });
        }
        this.on(window, 'popstate', () => {
          const key = new URL(location.href).searchParams.get('piece');
          if (this.products.some(p => p.key === key)) this.chooseProduct(key, false);
        });
        this.on(document, 'click', event => {
          const link = event.target.closest('a[data-tm-piece]');
          if (!link || event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          const url = new URL(link.href, location.href);
          if (url.origin !== location.origin || url.pathname !== location.pathname) return;
          event.preventDefault(); this.chooseProduct(link.dataset.tmPiece, true);
          this.scrollIntoView({ behavior: this.motion(), block: 'start' });
        });
        this.on(document, 'shopify:block:select', event => {
          const button = event.target.closest('[data-quote-product]');
          if (button && this.contains(button)) this.chooseProduct(button.dataset.quoteProduct, false);
        });
        const syncStudio = () => requestAnimationFrame(() => { const link = this.find('[data-quote-artwork]'); if (link) link.hidden = !this.product?.concept || !document.querySelector('tm-concept'); });
        this.on(document, 'shopify:section:load', syncStudio);
        this.on(document, 'shopify:section:unload', syncStudio);
        syncStudio();
        const art = this.find('[data-quote-artwork]');
        if (art) this.on(art, 'click', () => {
          const studio = document.querySelector('tm-concept');
          studio?.querySelector(`[data-garment="${this.state.product}"]`)?.click();
          const mapping = { natural: 'cream', white: 'cream', black: 'charcoal', grey: 'charcoal', navy: 'navy', green: 'olive', orange: 'rust' };
          if (mapping[this.state.colour]) studio?.querySelector(`[data-colour="${mapping[this.state.colour]}"]`)?.click();
        });
        this.chooseProduct(this.state.product, false, true);
        if (window.TMQuoteArtwork) this.artwork = new window.TMQuoteArtwork(this);
        this.dataset.ready = 'true';
        if (this.find('[data-quote-server-error]')) this.openContact(false);
        const feedback = this.find('[data-quote-success], [data-quote-server-error]');
        if (feedback) { feedback.focus({ preventScroll: true }); feedback.scrollIntoView({ block: 'center' }); }
      } catch (error) {
        this.find('[data-quote-price]').textContent = 'Please contact our team for a quote.';
        const button = this.find('[data-quote-open]');
        button.textContent = 'Contact our team';
        this.on(button, 'click', () => { location.href = '/#start-project'; });
      }
    }
    quoteStatus(message, error = false) {
      const status = this.find('[data-art-submit-status]');
      status.textContent = message; status.dataset.error = String(error);
    }
    releaseQuote() {
      clearTimeout(this.quoteWatchdog);
      this.quoteSending = false; this.quoteHandedOff = false;
      this.form?.removeAttribute('aria-busy');
      if (this.quoteSubmitButton) this.quoteSubmitButton.disabled = false;
    }
    waitForCaptcha() {
      // Only the supported Shopify API owns CAPTCHA. Never call hcaptcha directly
      // or fall back to HTMLFormElement.prototype.submit (which bypasses protection).
      return new Promise((resolve, reject) => {
        const signal = this.controller.signal;
        let settled = false, poll;
        const finish = error => {
          if (settled) return;
          settled = true; clearTimeout(timeout); clearTimeout(poll);
          signal.removeEventListener('abort', abort);
          error ? reject(error) : resolve();
        };
        const abort = () => finish(new Error('The form was closed. Please try again.'));
        const timeout = setTimeout(() => finish(new Error('Verification could not load. Your request has not been sent. Please check your connection and try again.')), 15000);
        signal.addEventListener('abort', abort, { once: true });
        const connect = () => {
          if (settled) return;
          const captcha = window.Shopify?.captcha;
          if (typeof captcha?.protect !== 'function') { poll = setTimeout(connect, 100); return; }
          try {
            captcha.protect(this.form, () => {
              // A failed provider load must not leave a raw native submit path.
              const protectedSubmit = this.form.submit;
              if (typeof protectedSubmit !== 'function' || protectedSubmit === HTMLFormElement.prototype.submit) {
                finish(new Error('Verification is not ready. Your request has not been sent. Please reload this page and try again.'));
              } else finish();
            });
          } catch (_) { finish(new Error('Verification could not start. Your request has not been sent. Please try again.')); }
        };
        connect();
      });
    }
    async sendQuote() {
      if (this.quoteSending) return;
      if (this.find('[data-quote-contact]').hidden) { this.openContact(); return; }
      if (!this.validSelections() || !this.form.reportValidity()) return;
      this.quoteSending = true; this.quoteHandedOff = false;
      this.quoteSubmitButton = this.form.querySelector('button[type=submit]');
      this.quoteSubmitButton.disabled = true; this.form.setAttribute('aria-busy', 'true');
      try {
        if (this.artwork && !(await this.artwork.prepareSubmission())) { this.releaseQuote(); return; }
        if (!this.isConnected) { this.releaseQuote(); return; }
        this.update(); this.saveDraft();
        this.quoteStatus('Files ready. Preparing secure submission…');
        await this.waitForCaptcha();
        if (!this.isConnected || !this.form.reportValidity()) { this.releaseQuote(); return; }
        this.update(); this.saveDraft();
        this.quoteStatus('Sending your request… Complete any verification that appears.');
        this.quoteHandedOff = true;
        // Keep the attempt locked if the CAPTCHA provider itself stalls: retrying
        // without a reload could let an earlier delayed verification send twice.
        this.quoteWatchdog = setTimeout(() => this.quoteStatus('Verification has not finished. Your request is not confirmed. Reload this page before trying again; your uploaded artwork and details have been saved.', true), 60000);
        // Shopify replaces this instance method with its protected submit handler.
        this.form.submit();
      } catch (error) {
        this.releaseQuote();
        if (this.isConnected) this.quoteStatus(error.message, true);
      }
    }
    draftFields() { return this.all('[data-quote-fields] input:not([type=hidden]), [data-quote-fields] textarea:not([hidden]), [data-quote-fields] select'); }
    restoreDraft() {
      this.draftKey = this.storageKey + ':contact';
      try {
        if (this.find('[data-quote-success]')) {
          sessionStorage.removeItem(this.draftKey);
          this.draftFields().forEach(field => { if (field.tagName === 'SELECT') field.selectedIndex = 0; else field.value = ''; });
          return;
        }
        const draft = JSON.parse(sessionStorage.getItem(this.draftKey) || 'null');
        if (!draft || !Number.isFinite(draft.savedAt) || Date.now() - draft.savedAt > 8 * 60 * 60 * 1000) {
          sessionStorage.removeItem(this.draftKey); return;
        }
        for (const field of this.draftFields()) {
          const value = draft.fields?.[field.name];
          if (typeof value !== 'string') continue;
          // A server-returned value takes precedence over the tab's draft.
          if (field.tagName !== 'SELECT' && field.value) continue;
          if (field.tagName === 'SELECT' && ![...field.options].some(option => option.value === value)) continue;
          field.value = value.slice(0, field.maxLength > 0 ? field.maxLength : 200);
        }
      } catch (_) { /* Storage is optional, including in private browsing. */ }
    }
    saveDraft() {
      try {
        const fields = Object.fromEntries(this.draftFields().map(field => [field.name, field.value]));
        sessionStorage.setItem(this.draftKey, JSON.stringify({ savedAt: Date.now(), fields }));
      } catch (_) {}
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    get product() { return this.products.find(p => p.key === this.state.product); }
    chooseProduct(key, updateURL, initial = false) {
      if (!this.products.some(p => p.key === key)) return;
      if (!initial && key !== this.state.product) { this.state.weight = 0; this.state.fit = 'normal'; }
      this.artwork?.invalidate();
      this.state.product = key; this.side = 'front';
      const product = this.product;
      this.all('[data-quote-product]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.quoteProduct === key)));
      this.find('[data-quote-select]').value = key;
      this.find('[data-quote-title]').textContent = product.title;
      this.find('[data-quote-description]').textContent = product.description;
      this.find('[data-quote-product-value]').value = product.title;
      this.find('[data-fit-legend]').textContent = 'Choose your ' + product.fitLabel.toLowerCase();
      this.find('[data-fabric-legend]').textContent = 'Choose your ' + product.fabricLabel.toLowerCase();
      this.find('[data-fabric-hint]').textContent = product.fabricHint;
      this.all('[data-quote-fit]').forEach(input => { input.nextElementSibling.textContent = product.fits[input.value]; });
      const artLink = this.find('[data-quote-artwork]'); if (artLink) artLink.hidden = !product.concept || !document.querySelector('tm-concept');
      const weights = this.find('[data-quote-weights]'); weights.replaceChildren();
      product.weights.forEach((weight, index) => {
        const label = document.createElement('label'), input = document.createElement('input'), span = document.createElement('span');
        input.type = 'radio'; input.name = 'contact[Fabric]'; input.value = weight.label; input.dataset.quoteWeight = index;
        span.textContent = weight.label; label.append(input, span); weights.append(label);
      });
      const quantity = this.find('[data-quote-quantity]');
      quantity.min = Number.isSafeInteger(Number(product.minimum)) && Number(product.minimum) > 0 ? product.minimum : 1;
      this.find('[data-quantity-note]').textContent = Number(quantity.min) > 1 ? `Minimum ${quantity.min} pieces. Size breakdown can be confirmed with your quote.` : 'Size breakdown can be confirmed with your quote.';
      this.all('[data-quote-qty]').forEach(button => { button.disabled = Number(button.dataset.quoteQty) < Number(quantity.min); });
      if (updateURL) {
        const url = new URL(location.href); url.searchParams.set('piece', key); url.hash = 'configure';
        history.replaceState(history.state, '', url);
      }
      this.update(); this.draw();
    }
    update() {
      const state = this.state;
      for (const [selector, field] of [['[data-quote-fit]', 'fit'], ['[data-quote-colour]', 'colour'], ['[data-quote-design]', 'design']]) this.all(selector).forEach(input => { input.checked = input.value === state[field]; });
      this.all('[data-quote-weight]').forEach(input => { input.checked = Number(input.dataset.quoteWeight) === state.weight; });
      const quantity = this.find('[data-quote-quantity]');
      if (quantity.value !== String(state.quantity)) quantity.value = state.quantity;
      this.all('[data-quote-qty]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.quoteQty) === Number(state.quantity))));
      const custom = state.colour === 'custom', pms = this.find('[data-quote-pms]');
      this.find('[data-pms-wrap]').hidden = !custom; pms.required = custom; pms.disabled = !custom; pms.value = state.pms;
      pms.setCustomValidity(custom && !state.pms.trim() ? 'Enter your PMS colour reference.' : '');
      this.colourName = this.find('[data-quote-colour]:checked').dataset.colourName;
      this.find('[data-colour-title]').textContent = this.colourName;
      this.find('[data-quote-colour-label]').textContent = this.colourName;
      const result = window.TMQuotePricing.calculate(this.product, state); this.result = result;
      const priced = result.status === 'priced';
      this.find('[data-price-label]').textContent = priced ? 'Estimated total' : 'Your project';
      this.find('[data-quote-price]').textContent = priced ? this.money(result.total) : 'Let’s price your idea.';
      let detail = 'Your selected specifications will be included in your quote request.';
      if (result.status === 'quantity') detail = `Enter a whole quantity between ${result.minimum} and 100,000.`;
      if (result.status === 'design') detail = 'Tell us if your design is ready to complete your brief.';
      if (result.status === 'pms') detail = 'Enter your PMS colour reference to complete your brief.';
      if (priced) detail = `${this.money(result.unit)} / piece × ${result.quantity}${result.design ? ` + ${this.money(result.design)} design support` : ''}${result.discount ? ` · ${result.discount}% volume discount included` : ''}`;
      this.find('[data-quote-price-detail]').textContent = detail;
      this.find('[data-price-disclaimer]').hidden = !priced;
      this.find('[data-quote-fit-value]').value = this.product.fits[state.fit];
      this.find('[data-quote-colour-value]').value = custom ? `Custom PMS: ${state.pms.trim()}` : this.colourName;
      this.find('[data-quote-design-value]').value = designs[state.design] || '';
      this.artwork?.sync();
      this.updateBrief();
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(state)); } catch (_) {}
    }
    money(cents) { return this.format.format(cents / 100); }
    rows() {
      const s = this.state;
      return [['Product', this.product.title], [this.product.fitLabel, this.product.fits[s.fit]], [this.product.fabricLabel, this.product.weights[s.weight].label], ['Colour', s.colour === 'custom' ? `Custom PMS: ${s.pms.trim() || 'Not entered'}` : this.colourName], ['Quantity', `${s.quantity || 'Not entered'} pieces`], ['Design ready?', designs[s.design] || 'Not selected']];
    }
    updateBrief() {
      if (!this.result) return;
      const rows = this.rows();
      for (const [label, selector] of [['Delivery destination', '[data-quote-destination]'], ['Target timing', '[data-quote-timing]'], ['Decoration', '[data-quote-decoration]']]) {
        const value = this.find(selector).value.trim(); if (value) rows.push([label, value]);
      }
      if (this.result.status === 'priced') rows.push(['Indicative estimate', `${this.money(this.result.total)} (${this.money(this.result.unit)} / piece; design support ${this.money(this.result.design)})`]);
      const displayRows = rows.slice();
      if (this.artwork) {
        rows.push(...this.artwork.briefRows());
        const files = this.artwork.productFiles();
        if (files.length) displayRows.push(['Your artwork', files.map(file => file.name).join(', ')]);
        if (this.artwork.placementText()) displayRows.push(['Your mockup', 'Front and back concept included with your artwork when file delivery is connected.']);
      }
      const summary = this.find('[data-quote-summary]'); summary.replaceChildren();
      displayRows.forEach(([label, value]) => { const dt = document.createElement('dt'), dd = document.createElement('dd'); dt.textContent = label; dd.textContent = value; summary.append(dt, dd); });
      const extras = [];
      for (const [label, selector] of [['Artwork link', '[data-quote-art-link]'], ['Additional details', '[data-quote-notes]']]) {
        const value = this.find(selector).value.trim(); if (value) extras.push(`${label}: ${value}`);
      }
      const disclaimer = (this.result.status === 'priced' ? 'Any estimate excludes VAT and delivery. ' : '') + 'Final specifications, decoration and pricing are confirmed by our team.';
      this.find('[data-quote-body]').value = ['TRENDMAKERS — CUSTOM APPAREL BRIEF', rows.map(row => row.join(': ')).join('\n'), extras.join('\n'), disclaimer].filter(Boolean).join('\n\n');
    }
    validSelections() {
      this.update();
      for (const field of this.all('[data-quote-quantity], [data-quote-pms], [data-quote-design]')) {
        if (!field.disabled && !field.checkValidity()) { field.reportValidity(); return false; }
      }
      return true;
    }
    openContact(validate = true) {
      if (validate && !this.validSelections()) return;
      const panel = this.find('[data-quote-contact]'); panel.hidden = false;
      this.find('[data-quote-fields]').disabled = false;
      this.find('[data-quote-open]').setAttribute('aria-expanded', 'true');
      if (validate) { panel.scrollIntoView({ behavior: this.motion(), block: 'start' }); this.find('[data-quote-name]').focus({ preventScroll: true }); }
    }
    // Cache decoded views and their visible bounds; transparent padding must not move a garment.
    loadView(src) {
      if (!this.images.has(src)) {
        this.images.set(src, new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => resolve(image);
          image.onerror = () => { this.images.delete(src); reject(new Error('Image unavailable')); };
          image.src = src;
        }));
      }
      return this.images.get(src);
    }
    viewBounds(image, sx, sw) {
      const full = {sx, sy:0, sw, sh:image.naturalHeight};
      try {
        const probe = document.createElement('canvas');
        probe.width = sw; probe.height = image.naturalHeight;
        const ctx = probe.getContext('2d', {willReadFrequently:true});
        ctx.drawImage(image, sx, 0, sw, probe.height, 0, 0, sw, probe.height);
        const pixels = ctx.getImageData(0,0,sw,probe.height).data;
        const columns = new Uint32Array(sw), rows = new Uint32Array(probe.height);
        for (let y=0; y<probe.height; y++) for (let x=0; x<sw; x++) {
          if (pixels[(y*sw+x)*4+3] >= 128) { columns[x]++; rows[y]++; }
        }
        // Ignore isolated alpha flecks without clipping the body's soft edges.
        const minColumn = Math.max(2, Math.max(...columns)*.025);
        const minRow = Math.max(2, Math.max(...rows)*.025);
        let left=0, right=sw-1, top=0, bottom=probe.height-1;
        while(left<right && columns[left]<minColumn) left++;
        while(right>left && columns[right]<minColumn) right--;
        while(top<bottom && rows[top]<minRow) top++;
        while(bottom>top && rows[bottom]<minRow) bottom--;
        if (right-left<8 || bottom-top<8) return full;
        return {sx:sx+left, sy:top, sw:right-left+1, sh:bottom-top+1};
      } catch { return full; } // Merchant photos without readable alpha still render normally.
    }
    async viewsFor(product) {
      this.viewCache ||= new Map();
      const key = JSON.stringify([product.atlas,product.front,product.back]);
      if (!this.viewCache.has(key)) {
        const promise = (async () => {
          if (!product.front) {
            const image = await this.loadView(product.atlas), half = Math.floor(image.naturalWidth/2);
            return ['front','back'].map((side,i) => ({side,image,...this.viewBounds(image,i*half,half)}));
          }
          const results = await Promise.allSettled([this.loadView(product.front), product.back ? this.loadView(product.back) : Promise.resolve(null)]);
          return results.map((result,i) => result.status==='fulfilled' && result.value ?
            {side:i?'back':'front',image:result.value,...this.viewBounds(result.value,0,result.value.naturalWidth)} : null).filter(Boolean);
        })();
        this.viewCache.set(key,promise);
        promise.catch(()=>this.viewCache.delete(key));
      }
      return this.viewCache.get(key);
    }
    async draw() {
      const product=this.product, side=this.side, colour=this.state.colour;
      const token=this.drawToken=(this.drawToken||0)+1;
      const hasBack=product.front?Boolean(product.back):Boolean(product.atlas);
      const canvas=this.find('[data-quote-canvas]'), fallback=this.find('[data-quote-fallback]');
      const stage=canvas.closest('.tm-config__stage'), made=!product.atlas&&!product.front;
      this.find('[data-quote-view="back"]').hidden=!hasBack;
      this.all('[data-quote-view]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.quoteView===side)));
      this.find('[data-visual-label]').textContent=`${product.title.replace('Full custom ','')} / ${side}`;
      this.find('[data-quote-caption]').textContent=colour==='custom' ?
        'PMS colour will be matched by our team. Preview shown in natural; fit and fabric are illustrative.' : product.front ?
        'Product reference image. Your chosen colour, fit and fabric are included in the brief.' :
        'Illustrative preview. Fit, fabric and exact colour are confirmed with your quote.';
      this.find('[data-quote-made]').hidden=!made;
      this.find('[data-quote-stamp]').hidden=made;
      this.find('[data-quote-made-title]').textContent=product.title.replace('Full custom ','').toUpperCase();
      this.find('[data-quote-view="front"]').hidden=made;
      if (made) {
        canvas.hidden=true; fallback.hidden=true; stage.removeAttribute('aria-busy');
        this.renderedProduct=null;
        this.find('[data-visual-label]').textContent='Full custom / Made to order';
        this.find('[data-quote-caption]').textContent='Your selected style, fabric direction and colour form the starting brief. Our team confirms the final design with you.';
        return;
      }
      // Keep the previous frame during a side/colour change. Never briefly show the raw atlas.
      if (this.renderedProduct!==product.key) canvas.hidden=true;
      fallback.hidden=true;
      stage.setAttribute('aria-busy','true');
      try {
        const views=await this.viewsFor(product);
        if(token!==this.drawToken||!this.isConnected) return;
        const view=views.find(v=>v.side===side);
        if(!view) throw new Error('Image unavailable');
        // Both sides share one centre line, one height and one baseline. No per-side zoom.
        const height=Math.min(780,750/Math.max(...views.map(v=>v.sw/v.sh)));
        const width=height*view.sw/view.sh, x=(900-width)/2, y=(940-height)/2;
        const frame=document.createElement('canvas'); frame.width=900; frame.height=940;
        const c=frame.getContext('2d');
        const paint=()=>c.drawImage(view.image,view.sx,view.sy,view.sw,view.sh,x,y,width,height);
        paint();
        if(!product.front) {
          c.globalCompositeOperation='source-in'; c.fillStyle=colours[colour]; c.fillRect(0,0,900,940);
          c.globalCompositeOperation='multiply'; paint();
          c.globalCompositeOperation='destination-in'; paint();
          c.globalCompositeOperation='source-over';
        }
        this.artwork?.paint(c, product.key, side);
        const target=canvas.getContext('2d');
        target.clearRect(0,0,canvas.width,canvas.height); target.drawImage(frame,0,0);
        canvas.setAttribute('aria-label',`${product.title}, ${colour==='custom'?'natural reference for custom PMS':colour}, ${side} view. Illustrative concept.`);
        canvas.hidden=false; fallback.hidden=true; this.renderedProduct=product.key;
        stage.removeAttribute('aria-busy');
        this.artwork?.sync();
      } catch {
        if(token!==this.drawToken) return;
        canvas.hidden=true; this.renderedProduct=null; stage.removeAttribute('aria-busy');
        const src=product.front?(side==='back'?product.back:product.front):product.atlas;
        const image=fallback.querySelector('img'); image.src=src; image.alt=product.title+' '+side+' reference';
        fallback.classList.toggle('tm-config__fallback--single',Boolean(product.front));
        fallback.classList.toggle('tm-config__fallback--back',side==='back'); fallback.hidden=false;
        this.find('[data-quote-caption]').textContent='Preview could not be prepared. Your selections are still included in your quote request.';
      }
    }
    disconnectedCallback() { this.releaseQuote(); this.artwork?.destroy(); this.artwork = null; this.controller?.abort(); this.controller = null; this.drawToken = (this.drawToken || 0) + 1; }
  }
  customElements.define('tm-quote-builder', QuoteBuilder);
})();
